import type { ProductFilters } from '~/types'

export function useFilters() {
  const route = useRoute()
  const router = useRouter()

  // Parse filters from URL query params
  const parseFiltersFromUrl = (): ProductFilters => {
    const query = route.query

    return {
      search: query.q as string | undefined,
      categoryId: query.categoria as string | undefined,
      productLineId: query.linea as string | undefined,
      componentType: query.tipo as string | undefined,
      minPackSize: query.min_size ? Number(query.min_size) : undefined,
      maxPackSize: query.max_size ? Number(query.max_size) : undefined
    }
  }

  // Reactive filters state synced with URL
  const filters = reactive<ProductFilters>(parseFiltersFromUrl())

  // Watch route changes to update filters - individual assignments for better reactivity
  watch(() => route.query, () => {
    const parsed = parseFiltersFromUrl()
    filters.search = parsed.search
    filters.categoryId = parsed.categoryId
    filters.productLineId = parsed.productLineId
    filters.componentType = parsed.componentType
    filters.minPackSize = parsed.minPackSize
    filters.maxPackSize = parsed.maxPackSize
  })

  // Update URL when filters change
  const updateUrl = () => {
    const query: Record<string, string> = {}

    if (filters.search) query.q = filters.search
    if (filters.categoryId) query.categoria = filters.categoryId
    if (filters.productLineId) query.linea = filters.productLineId
    if (filters.componentType) query.tipo = filters.componentType
    if (filters.minPackSize) query.min_size = String(filters.minPackSize)
    if (filters.maxPackSize) query.max_size = String(filters.maxPackSize)

    router.push({ query })
  }

  // Update a single filter
  const setFilter = <K extends keyof ProductFilters>(
    key: K,
    value: ProductFilters[K]
  ) => {
    filters[key] = value
    updateUrl()
  }

  // Clear all filters
  const clearFilters = () => {
    filters.search = undefined
    filters.categoryId = undefined
    filters.productLineId = undefined
    filters.componentType = undefined
    filters.minPackSize = undefined
    filters.maxPackSize = undefined
    router.push({ query: {} })
  }

  // Check if any filters are active
  const hasActiveFilters = computed(() => {
    return !!(
      filters.search ||
      filters.categoryId ||
      filters.productLineId ||
      filters.componentType ||
      filters.minPackSize ||
      filters.maxPackSize
    )
  })

  return {
    filters: readonly(filters),
    setFilter,
    clearFilters,
    hasActiveFilters,
    updateUrl
  }
}
